import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/homeData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
              <i className="ri-emotion-sad-line text-3xl"></i>
            </div>
            <h1 className="mt-6 text-2xl font-heading font-bold text-foreground-900">文章不存在</h1>
            <p className="mt-2 text-foreground-500">你要找的文章好像不见了~</p>
            <Link
              to="/"
              className="inline-block mt-6 px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.slug !== article.slug)
    .slice(0, 3);

  return (
    <>
      <Navbar />
      <main>
        {/* Breadcrumb */}
        <div className="pt-20 pb-0 bg-background-50">
          <div className="container-custom">
            <nav className="py-4" aria-label="面包屑导航">
              <ol className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
                <li>
                  <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">
                    首页
                  </Link>
                </li>
                <li className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line text-xs"></i>
                </li>
                <li>
                  <Link to={`/category/${article.categorySlug}`} className="hover:text-primary-500 transition-colors cursor-pointer">
                    {article.category}
                  </Link>
                </li>
                <li className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line text-xs"></i>
                </li>
                <li className="text-foreground-700 line-clamp-1">{article.title}</li>
              </ol>
            </nav>
          </div>
        </div>

        {/* Article Hero */}
        <section className="bg-background-50">
          <div className="container-custom py-6">
            <div className="max-w-3xl mx-auto">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 mt-6 pb-6 border-b border-background-200/70">
                <div className="w-10 h-10 rounded-full overflow-hidden">
                  <img
                    src={article.authorAvatar}
                    alt={article.author}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-800">{article.author}</p>
                  <p className="text-xs text-foreground-500">
                    {article.publishDate} · 阅读 {article.readTime}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto">
              <div className="w-full aspect-video rounded-lg overflow-hidden mb-10">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <article className="prose-custom">
                {article.content.map((paragraph, idx) => (
                  <p
                    key={idx}
                    className="text-base leading-relaxed text-foreground-700 mb-6"
                  >
                    {paragraph}
                  </p>
                ))}
              </article>

              {/* Share */}
              <div className="mt-12 pt-8 border-t border-background-200/70">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-foreground-500">分享这篇文章：</span>
                  <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微博">
                    <i className="ri-weibo-line text-base"></i>
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微信">
                    <i className="ri-wechat-line text-base"></i>
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="复制链接">
                    <i className="ri-link text-base"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-14 md:py-20 bg-background-100">
            <div className="container-custom">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.slug}
                    to={`/article/${rel.slug}`}
                    className="group bg-background-50 rounded-lg overflow-hidden cursor-pointer transition-all duration-300 hover:scale-[1.02]"
                  >
                    <div className="relative w-full h-40 overflow-hidden">
                      <img
                        src={rel.coverImage}
                        alt={rel.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-heading font-bold text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                        {rel.title}
                      </h3>
                      <p className="mt-1 text-xs text-foreground-500">
                        {rel.publishDate} · {rel.readTime}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'BreadcrumbList',
                '@id': `https://ruilongguoyao.cn/article/${slug}/#breadcrumb`,
                itemListElement: [
                  {
                    '@type': 'ListItem',
                    position: 1,
                    name: '首页',
                    item: 'https://ruilongguoyao.cn/',
                  },
                  {
                    '@type': 'ListItem',
                    position: 2,
                    name: article.category,
                    item: `https://ruilongguoyao.cn/category/${article.categorySlug}/`,
                  },
                  {
                    '@type': 'ListItem',
                    position: 3,
                    name: article.title,
                    item: `https://ruilongguoyao.cn/article/${slug}/`,
                  },
                ],
              },
              {
                '@type': 'Article',
                '@id': `https://ruilongguoyao.cn/article/${slug}/#article`,
                url: `https://ruilongguoyao.cn/article/${slug}/`,
                headline: article.title,
                description: article.excerpt,
                image: article.coverImage,
                author: {
                  '@type': 'Person',
                  name: article.author,
                },
                publisher: {
                  '@id': 'https://ruilongguoyao.cn/#organization',
                },
                datePublished: article.publishDate,
                inLanguage: 'zh-CN',
                isPartOf: {
                  '@id': 'https://ruilongguoyao.cn/#website',
                },
              },
            ],
          }),
        }}
      />
    </>
  );
}